"use client";

import Link from "next/link";
import { useCallback, useContext, useState } from "react";
import { useSession } from "next-auth/react";

import { cn } from "@/lib/utils";
import { trackInitiateCheckoutPixel } from "@/lib/facebookClient";
import { trackClientEvent } from "@/lib/analyticsClient";
import { generateUserStripe } from "@/actions/generate-user-stripe";
import { pricingData } from "@/config/subscriptions";
import { ModalContext } from "@/components/modals/providers";

interface TrialCTAProps {
  href?: string;
  children?: React.ReactNode;
  className?: string;
  ctaLocation?: string;
  planType?: string;
  planValue?: number;
  onClick?: () => void;
  /**
   * When true, the button goes straight into the $1 trial checkout instead of
   * routing to /pricing. Authenticated users are sent directly to Stripe;
   * unauthenticated users get the sign-in modal with a stored trial intent so
   * they land in checkout right after auth. Defaults to the PRO monthly plan.
   */
  direct?: boolean;
}

const DEFAULT_HREF = "/pricing";

const PRO_MONTHLY_STRIPE_ID =
  pricingData.find((p) => p.title === "QuantWheel PRO")?.stripeIds.monthly || null;

const DEFAULT_CLASSES =
  "inline-flex items-center justify-center rounded-full bg-gradient-to-r from-lime-300 to-green-300 px-6 h-11 text-base font-semibold text-zinc-900 shadow-md shadow-lime-400/20 transition-all duration-200 hover:from-lime-200 hover:to-green-200 hover:shadow-lg hover:shadow-lime-400/30";

function fireGoogleAdsEvents(planValue: number) {
  try {
    if (typeof window === "undefined") return;
    const gtag = (window as any).gtag;
    if (typeof gtag !== "function") return;

    gtag("event", "begin_checkout", {
      currency: "USD",
      value: planValue,
    });

    const sendTo = process.env.NEXT_PUBLIC_GOOGLE_ADS_BEGIN_CHECKOUT_SEND_TO;
    if (sendTo) {
      gtag("event", "conversion", {
        send_to: sendTo,
        value: planValue,
        currency: "USD",
      });
    }
  } catch (err) {
    console.warn(JSON.stringify({ event: "trial_cta_gtag_error", error: (err as any)?.message }));
  }
}

export function TrialCTA({
  href = DEFAULT_HREF,
  children = "Start 7\u2011Days for $1",
  className,
  ctaLocation = "unknown",
  planType = "pro",
  planValue = 1.0,
  onClick,
  direct = false,
}: TrialCTAProps) {
  const { status } = useSession();
  const { setShowSignInModal } = useContext(ModalContext);
  const [isPending, setIsPending] = useState(false);

  const fireTracking = useCallback(() => {
    try {
      trackInitiateCheckoutPixel(planType, planValue);
    } catch (err) {
      console.warn(JSON.stringify({ event: "trial_cta_pixel_error", error: (err as any)?.message }));
    }
    fireGoogleAdsEvents(planValue);
    trackClientEvent({
      name: "trial_cta_clicked",
      params: { cta_location: ctaLocation, plan_type: planType, plan_value: planValue, direct },
    });
    onClick?.();
  }, [ctaLocation, planType, planValue, onClick, direct]);

  const handleDirectClick = useCallback(async () => {
    fireTracking();

    // Authenticated users go straight to Stripe checkout for the PRO monthly trial.
    if (status === "authenticated" && PRO_MONTHLY_STRIPE_ID) {
      try {
        setIsPending(true);
        await generateUserStripe(PRO_MONTHLY_STRIPE_ID);
        return;
      } catch (err) {
        console.warn(JSON.stringify({ event: "trial_cta_stripe_error", error: (err as any)?.message }));
        setIsPending(false);
        window.location.href = DEFAULT_HREF;
        return;
      }
    }

    // Unauthenticated users: store intent so they land in checkout after sign-in.
    if (typeof window !== "undefined") {
      sessionStorage.setItem("trialIntent", "pro|monthly");
    }
    setShowSignInModal(true);
  }, [fireTracking, status, setShowSignInModal]);

  if (direct) {
    return (
      <button
        type="button"
        onClick={handleDirectClick}
        disabled={isPending}
        className={cn(DEFAULT_CLASSES, "disabled:opacity-70", className)}
      >
        {isPending ? "Redirecting\u2026" : children}
      </button>
    );
  }

  return (
    <Link
      href={href}
      prefetch={true}
      onClick={fireTracking}
      className={cn(DEFAULT_CLASSES, className)}
    >
      {children}
    </Link>
  );
}

interface SandboxCTAProps {
  href?: string;
  children?: React.ReactNode;
  className?: string;
  ctaLocation?: string;
  onClick?: () => void;
}

const DEFAULT_SANDBOX_CLASSES =
  "inline-flex items-center justify-center rounded-full border border-white/20 bg-transparent px-6 h-11 text-base font-medium text-white backdrop-blur-sm transition-all duration-200 hover:border-white/40 hover:bg-white/10";

export function SandboxCTA({
  href = "/dashboard/wheel",
  children = "Try Sandbox",
  className,
  ctaLocation = "unknown",
  onClick,
}: SandboxCTAProps) {
  const handleClick = useCallback(() => {
    try {
      if (typeof window !== "undefined" && typeof (window as any).fbq === "function") {
        (window as any).fbq("trackCustom", "TrySandbox", { cta_location: ctaLocation });
      }
    } catch (err) {
      console.warn(JSON.stringify({ event: "sandbox_cta_pixel_error", error: (err as any)?.message }));
    }
    trackClientEvent({
      name: "sandbox_cta_clicked",
      params: { cta_location: ctaLocation },
    });
    onClick?.();
  }, [ctaLocation, onClick]);

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={cn(DEFAULT_SANDBOX_CLASSES, className)}
    >
      {children}
    </Link>
  );
}
